// One device on the Tuya Testing bench — the card in the Flutter screenshot,
// used by BOTH shells.
//
// ── Two densities, one component ───────────────────────────────────────────
//
// `dense` is the DESKTOP layout, and it is not just smaller padding. A phone
// shows one card at a time, so spelling everything out costs nothing; a desktop
// shows a dozen at once, and the thing a technician does there is SCAN — which
// row is red, which one has not reported, which one is on the wrong type. So in
// dense mode:
//
//   • the four header lines collapse to a name + a type chip + one tiny
//     timestamp line, and DeviceName is dropped when it is just "Smartify_<uno>"
//     (which it almost always is — it was repeating the title back)
//   • the five text buttons become icons with tooltips, on the same row as the
//     type dropdown, so the controls take one row instead of three
//   • the raw frame is one ellipsised monospace line, full text on hover
//
// Nothing is REMOVED, only re-spelled: every control and every value is still
// on the card. That matters — this is a bench, and a hidden control is a
// control the technician goes looking for in the Flutter app instead.
//
// ⚠️ THE TILES ARE NOT OPTIMISTIC. A tap publishes and then waits; the bulb only
// changes colour when the device reports. On a bench that is the point — if the
// relay does not move, the operator must see that it did not move.

import React, { useState } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, Slider, MenuItem, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, useTheme,
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ToysIcon from '@mui/icons-material/Toys';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import DeviceWifiPanel from '../../desktop/components/DeviceWifiPanel';
import {
  tuyaKind, tuyaFanStep, tuyaFanSliderValue, TUYA_FAN_MAX, TUYA_FAN_STEP,
  shortAge, SEEN_LIVE, SEEN_LATE, SEEN_GONE, TUYA_HEARTBEAT_MS,
} from '../../../core/devices/tuyaTesting';
import { fitDialog } from '../fitDialog';

// `2026-08-03T13:21:11.000Z` -> `2026-08-03 13:21:11.000`, the Dart's own
// formatting. Deliberately NOT localised: the technician is comparing this
// against a server log, so it must read the same as the server log.
const stamp = (v) => (v ? String(v).replace('T', ' ').replace('Z', '') : '—');

// Same string minus the milliseconds, for the dense header. The YEAR STAYS —
// a device last seen in 2025 must not read like one seen this morning.
const shortStamp = (v) => (v ? stamp(v).slice(0, 19) : '—');

const ON_COLOUR = '#4CAF50';
const OFF_COLOUR = '#E53935';

// The liveness chip. "Late", not "offline", between one missed beat and the
// app's own ten-minute window — a device that missed a single heartbeat is
// usually still there, and calling that offline is what made an earlier version
// of the room screen cry wolf. See the ladder in core/devices/tuyaTesting.
const SEEN_STYLE = {
  [SEEN_LIVE]: { colour: '#4CAF50', word: 'live' },
  [SEEN_LATE]: { colour: '#FFB74D', word: 'late' },
  [SEEN_GONE]: { colour: '#E53935', word: 'gone' },
  never: { colour: '#9E9E9E', word: 'never seen' },
};

const clockOf = (ms) => (ms == null ? 'never'
  : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' }));

export default function TuyaDeviceCard({
  row, values = [], message = '', types = [], dense = false,
  live = null,
  onToggleChannel, onFanSpeed, onAllOn, onAllOff, onUnlock,
  onDeviceDetails, onUpdateCode, onChangeType,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const [wifiOpen, setWifiOpen] = useState(false);
  const [busyType, setBusyType] = useState(false);

  const uno = String(row.UniqueNumber);
  const kind = tuyaKind(row.DeviceID);
  const pad = dense ? 1 : 1.5;

  const btnSx = {
    bgcolor: c.back, color: c.text, textTransform: 'none', borderRadius: 999,
    px: 2, fontSize: 13, '&:hover': { bgcolor: c.back, filter: 'brightness(1.2)' },
  };
  const iconBtnSx = {
    bgcolor: c.back, color: c.text, borderRadius: '8px', p: 0.6,
    '&:hover': { bgcolor: c.back, filter: 'brightness(1.3)' },
  };

  // The installed name is nearly always "Smartify_<uno>", i.e. the title again.
  // Shown only when it is actually something else.
  const named = row.DeviceName && row.DeviceName !== `Smartify_${uno}`;

  // How long since we last heard anything at all from this device — a status, a
  // heartbeat, an OTA line. `live` comes from the bench hook, which ticks every
  // second, so this counts up on screen while a device is going quiet.
  const seen = SEEN_STYLE[live?.state] || SEEN_STYLE.never;
  const seenChip = (
    <Tooltip
      title={live?.at
        ? `Last heard from at ${clockOf(live.at)}. The firmware heartbeats every `
          + `${TUYA_HEARTBEAT_MS / 60000} minutes and stops the moment it loses the server.`
        : 'Nothing has been heard from this device since the list was loaded.'}
    >
      <Chip
        size="small"
        label={`${shortAge(live?.age)}${dense ? '' : ` · ${seen.word}`}`}
        sx={{
          height: dense ? 18 : 22,
          fontSize: dense ? 10 : 12,
          fontWeight: 700,
          bgcolor: `${seen.colour}22`,
          color: seen.colour,
          border: `1px solid ${seen.colour}66`,
        }}
      />
    </Tooltip>
  );

  const header = dense ? (
    <Box sx={{ px: pad, pt: pad, color: c.text }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {/* Liveness FIRST on a dense card. Scanning a rack for the one that
            dropped is the job, and a chip at the end of a truncated name is a
            chip nobody finds. */}
        {seenChip}
        <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {named ? row.DeviceName : `Smartify_${uno}`}
        </Typography>
        {row.DeviceTypeName && (
          <Chip
            size="small" label={row.DeviceTypeName}
            sx={{ height: 18, fontSize: 10, bgcolor: c.back, color: c.text }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: 10, opacity: 0.65, lineHeight: 1.5,
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        }}
      >
        {`${named ? `${uno} · ` : ''}upd ${shortStamp(row.LastUpdatedDateTime)}`}
        <br />
        {`code ${shortStamp(row.CodeUpdated)}`}
      </Typography>
    </Box>
  ) : (
    <Box sx={{ px: pad, pt: pad, color: c.text }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{`Smartify_${uno}`}</Typography>
        {seenChip}
      </Box>
      <Typography sx={{ fontSize: 13 }}>{`DeviceName:-${row.DeviceName ?? ''}`}</Typography>
      <Typography sx={{ fontSize: 13 }}>{`LastUpdated:-${stamp(row.LastUpdatedDateTime)}`}</Typography>
      <Typography sx={{ fontSize: 13 }}>{`LastCodeUpdated:-${stamp(row.CodeUpdated)}`}</Typography>
    </Box>
  );

  // The dropdown is keyed on DeviceID, not on the name the way the Dart is.
  // Flutter looks the selection up by its index in a parallel name array, so two
  // types sharing a name would swap silently; an id cannot.
  const typeSelect = (
    <TextField
      select size="small" fullWidth
      value={types.some((t) => Number(t.DeviceID) === Number(row.DeviceID)) ? Number(row.DeviceID) : ''}
      disabled={busyType || !types.length}
      onChange={async (e) => {
        setBusyType(true);
        try { await onChangeType(uno, Number(e.target.value)); } finally { setBusyType(false); }
      }}
      variant="standard"
      SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: c.back } } } }}
      sx={{
        '& .MuiInputBase-input': { color: c.text, fontSize: dense ? 12 : 13, py: dense ? 0.25 : 0.5 },
        '& .MuiInput-underline:before': { borderBottomColor: `${c.text}66` },
        '& .MuiSvgIcon-root': { color: c.text },
      }}
    >
      {types.map((t) => (
        <MenuItem key={t.DeviceID} value={Number(t.DeviceID)} sx={{ color: c.text, fontSize: 13 }}>
          {t.DeviceName}
        </MenuItem>
      ))}
    </TextField>
  );

  const relayRow = (
    <Box sx={{ display: 'flex', gap: dense ? 0.75 : 1, px: pad, py: dense ? 0.75 : 1, overflowX: 'auto' }}>
      {values.length === 0 && (
        <Typography sx={{ color: `${c.text}99`, fontSize: dense ? 11 : 13, py: dense ? 1 : 2 }}>
          No status reported yet.
        </Typography>
      )}
      {values.map((v, i) => (
        <Box
          // No id in a status frame — position IS the identity, so the index is
          // the only key there is.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          onClick={() => onToggleChannel(uno, i)}
          sx={{
            bgcolor: c.back, borderRadius: dense ? '8px' : '10px', cursor: 'pointer',
            minWidth: dense ? 46 : 74, py: dense ? 0.6 : 1.25,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: dense ? 0 : 0.5, userSelect: 'none',
            '&:active': { filter: 'brightness(1.3)' },
          }}
        >
          <LightbulbOutlinedIcon
            sx={{ fontSize: dense ? 26 : 40, color: String(v) === '0' ? OFF_COLOUR : ON_COLOUR }}
          />
          <Typography sx={{ fontSize: dense ? 9 : 10, fontWeight: 700, color: c.text }}>
            {`S${i + 1}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const fanRow = (
    <Box sx={{ px: dense ? pad : 2, py: dense ? 0.5 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ToysIcon sx={{ fontSize: dense ? 26 : 45, color: '#AB47BC' }} />
        {!dense && <Typography sx={{ color: c.text, fontSize: 13 }}>FAN SWITCH</Typography>}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: dense ? 22 : 40, color: ON_COLOUR, lineHeight: 1 }}>
          {tuyaFanStep(values[0])}
        </Typography>
        <Typography sx={{ color: c.text, fontSize: dense ? 11 : 13 }}>speed</Typography>
      </Box>
      <Slider
        value={tuyaFanSliderValue(values[0])}
        min={0} max={TUYA_FAN_MAX} step={TUYA_FAN_STEP}
        marks valueLabelDisplay="auto"
        size={dense ? 'small' : 'medium'}
        onChange={(_, v) => onFanSpeed(uno, v)}
        sx={{ color: c.text, mt: dense ? 0 : 1 }}
      />
    </Box>
  );

  // A door lock has no relay to show — one action, and the raw frame below says
  // what the lock answered. 3 sends OPENDOOR, 102 LOCKUNLOCK (the hook decides).
  const lockRow = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: pad, py: dense ? 0.75 : 1.5 }}>
      <LockOpenIcon sx={{ fontSize: dense ? 24 : 36, color: ON_COLOUR }} />
      <Button
        size="small" sx={btnSx}
        disabled={!onUnlock}
        onClick={() => onUnlock && onUnlock(uno, row.DeviceID)}
      >
        Unlock
      </Button>
    </Box>
  );
  const hasAll = kind !== 'fan' && kind !== 'lock';

  // Dense: one row of icons. Roomy: the Dart's two rows of labelled buttons.
  const controls = dense ? (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, px: pad }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{typeSelect}</Box>
      {hasAll && (
        <>
          <Tooltip title="All On">
            <IconButton size="small" sx={iconBtnSx} onClick={() => onAllOn(uno)}>
              <PowerSettingsNewIcon sx={{ fontSize: 17, color: ON_COLOUR }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="All Off">
            <IconButton size="small" sx={iconBtnSx} onClick={() => onAllOff(uno)}>
              <PowerOffIcon sx={{ fontSize: 17, color: OFF_COLOUR }} />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="Device Details (s05)">
        <IconButton size="small" sx={iconBtnSx} onClick={() => onDeviceDetails(uno)}>
          <InfoOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Update Code — reflashes and reboots this device">
        <IconButton size="small" sx={iconBtnSx} onClick={() => onUpdateCode(uno)}>
          <SystemUpdateAltIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Add Wi-Fi">
        <IconButton size="small" sx={iconBtnSx} onClick={() => setWifiOpen(true)}>
          <WifiIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    </Box>
  ) : (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, px: pad, flexWrap: 'wrap' }}>
        {hasAll && (
          <>
            <Button size="small" sx={btnSx} onClick={() => onAllOn(uno)}>All On</Button>
            <Button size="small" sx={btnSx} onClick={() => onAllOff(uno)}>All Off</Button>
          </>
        )}
        <Box sx={{ flex: 1, minWidth: 160 }}>{typeSelect}</Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, px: pad, pt: 1.5, flexWrap: 'wrap' }}>
        <Button size="small" sx={btnSx} onClick={() => onDeviceDetails(uno)}>Device Details</Button>
        <Button size="small" sx={btnSx} onClick={() => onUpdateCode(uno)}>Update Code</Button>
        <Button size="small" sx={btnSx} onClick={() => setWifiOpen(true)}>Add Wifi</Button>
      </Box>
    </>
  );

  const frame = message || row.Status || '';

  return (
    <Box sx={{ bgcolor: c.button, borderRadius: dense ? '8px' : '4px', pb: dense ? 0.75 : 1 }}>
      {header}

      {/* eslint-disable-next-line no-nested-ternary */}
      {kind === 'lock' ? lockRow : kind === 'fan' ? fanRow : kind ? relayRow : (
        <Typography sx={{ color: `${c.text}99`, fontSize: dense ? 11 : 13, px: pad, py: dense ? 1 : 2 }}>
          {`DeviceType ${row.DeviceID} has no bench control — pick the right type below.`}
        </Typography>
      )}

      {controls}

      {/* The raw last frame, verbatim. It is the only place a malformed status
          is visible at all — the tiles above have already interpreted it, so it
          is truncated on a narrow card but never dropped. */}
      <Tooltip title={dense ? frame : ''} placement="bottom-start">
        <Typography
          sx={{
            color: c.text, fontSize: dense ? 10 : 12, px: pad,
            pt: dense ? 0.75 : 1.5, opacity: dense ? 0.75 : 1,
            textAlign: dense ? 'left' : 'center',
            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            ...(dense
              ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
              : { wordBreak: 'break-all' }),
          }}
        >
          {frame}
        </Typography>
      </Tooltip>

      <Dialog
        open={wifiOpen} onClose={() => setWifiOpen(false)}
        {...fitDialog({ bgcolor: c.back }, 'sm')}
      >
        <DialogTitle sx={{ color: c.text }}>{`Wi-Fi — Smartify_${uno}`}</DialogTitle>
        <DialogContent>
          {/* The same panel the device screen uses: `getallwifi` to list,
              `ADDWIFIssid=…pass=…` to add. The literals live in
              core/devices/commands and are not re-spelled here. */}
          <DeviceWifiPanel device={{ UniqueNumber: uno }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWifiOpen(false)} sx={{ color: c.text }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
